import { colorFields, dividerField } from "./_shared";
/**
 * Value strip — the four-up "trust badges" row under the hero (same-day
 * delivery, returns, cash on delivery, WhatsApp support, …). Each badge is a
 * `value-item` block so the seller can edit the icon, title, and subtitle, add
 * or remove badges, and reorder them. Locked + home-only: it's a permanent
 * home section the seller controls (edit / hide / reorder) but can't delete —
 * matching the other locked home sections. It used to be a hardcoded strip
 * that sellers couldn't touch.
 */
export const valueStripSchema = {
    type: "value-strip",
    name: { ar: "شريط المميزات", en: "Value strip" },
    description: {
        ar: "صف من المميزات (توصيل، إرجاع، دفع عند الاستلام، دعم) تقدر تعدّله بالكامل.",
        en: "Row of trust badges (delivery, returns, COD, support) — fully editable.",
    },
    // Unlocked: deletable + re-addable from the editor's "+ Add section" picker.
    availableOnTemplates: ["home"],
    maxPerTemplate: 1,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع الشريط", en: "Strip style" },
            helpText: {
                ar: "اختر شكل عرض المميزات.",
                en: "Pick how the trust badges are displayed.",
            },
            options: [
                { value: "row", label: { ar: "صف", en: "Row" } },
                { value: "chips", label: { ar: "شارات", en: "Pills" } },
                { value: "cards", label: { ar: "بطاقات", en: "Cards" } },
                { value: "inline", label: { ar: "سطر واحد", en: "Inline" } },
                { value: "stack", label: { ar: "عمودي", en: "Stacked" } },
            ],
            default: "row",
        },
        // A4: the `cards` variant draws its own framed cards and omits the divider,
        // so the divider toggle is a no-op there.
        dividerField({ variant: ["row", "chips", "inline", "stack"] }),
        // The entrance animation is now provided by the shared animation group
        // (injected into every section by the registry), so the value strip no
        // longer declares its own `animation` field. Legacy stored values
        // (`slide`, `stagger`) are remapped to `slide-up` by the v1→v2 migration.
        ...colorFields(),
    ],
    blocks: [
        {
            type: "value-item",
            name: { ar: "ميزة", en: "Value badge" },
            description: {
                ar: "أيقونة وعنوان ووصف قصير.",
                en: "An icon with a title and a short subtitle.",
            },
            maxPerSection: 6,
            settings: [
                {
                    id: "icon",
                    type: "select",
                    ui: "icon",
                    label: { ar: "الأيقونة", en: "Icon" },
                    options: [
                        {
                            value: "truck",
                            label: { ar: "شاحنة (توصيل)", en: "Truck (delivery)" },
                        },
                        { value: "returns", label: { ar: "إرجاع", en: "Returns" } },
                        {
                            value: "shield",
                            label: { ar: "درع (دفع آمن)", en: "Shield (secure)" },
                        },
                        { value: "whatsapp", label: { ar: "واتساب", en: "WhatsApp" } },
                        { value: "clock", label: { ar: "ساعة", en: "Clock" } },
                        { value: "star", label: { ar: "نجمة", en: "Star" } },
                        { value: "gift", label: { ar: "هدية", en: "Gift" } },
                        { value: "lock", label: { ar: "قفل", en: "Lock" } },
                        {
                            value: "headset",
                            label: { ar: "سماعة دعم", en: "Support headset" },
                        },
                        { value: "tag", label: { ar: "وسم سعر", en: "Price tag" } },
                        { value: "card", label: { ar: "بطاقة", en: "Card" } },
                        { value: "cash", label: { ar: "نقدي", en: "Cash" } },
                        { value: "phone", label: { ar: "هاتف", en: "Phone" } },
                        { value: "mail", label: { ar: "بريد", en: "Email" } },
                        { value: "pin", label: { ar: "موقع", en: "Location" } },
                        { value: "box", label: { ar: "علبة", en: "Package" } },
                        { value: "heart", label: { ar: "قلب", en: "Heart" } },
                        { value: "verified", label: { ar: "موثّق", en: "Verified" } },
                        { value: "percent", label: { ar: "خصم", en: "Discount" } },
                        { value: "globe", label: { ar: "عالمي", en: "Worldwide" } },
                        { value: "leaf", label: { ar: "صديق للبيئة", en: "Eco" } },
                        { value: "sparkle", label: { ar: "مميز", en: "Premium" } },
                        { value: "award", label: { ar: "جائزة", en: "Award" } },
                        { value: "bolt", label: { ar: "سريع", en: "Fast" } },
                        { value: "chat", label: { ar: "دردشة", en: "Chat" } },
                        { value: "wallet", label: { ar: "محفظة", en: "Wallet" } },
                        { value: "calendar", label: { ar: "تقويم", en: "Calendar" } },
                        { value: "users", label: { ar: "مجتمع", en: "Community" } },
                    ],
                    default: "truck",
                },
                {
                    id: "title",
                    type: "text",
                    i18n: true,
                    label: { ar: "العنوان", en: "Title" },
                    validate: { max: 40 },
                },
                {
                    id: "subtitle",
                    type: "text",
                    i18n: true,
                    label: { ar: "الوصف", en: "Subtitle" },
                    // C4: `ChipsStrip` (pills) and `InlineStrip` (dot-separated line) render
                    // icon + title only — both are single-line formats by design. Row, Cards
                    // and Stacked render it via `ItemSubtitle` (`BuyerValueStrip.tsx`).
                    helpText: {
                        ar: "بيظهر في «صف» و«بطاقات» و«عمودي». «شارات» و«سطر واحد» بيخلّوا كل عنصر سطر واحد.",
                        en: "Shown in the Row, Cards, and Stacked styles. Pills and Inline keep each item to a single line.",
                    },
                    validate: { max: 60 },
                },
            ],
        },
    ],
};
//# sourceMappingURL=value-strip.js.map